/**
 * Barbers list page
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBarber } from '@/contexts/BarberContext';
import { useFranchise } from '@/contexts/FranchiseContext';
import { useBranch } from '@/contexts/BranchContext';
import { useAuth } from '@/contexts/AuthContext';
import { BarberCard } from '@/components/barbers/BarberCard';
import { BarberListView } from '@/components/barbers/BarberListView';
import { LoadingState } from '@/components/shared/LoadingState';
import { EmptyState } from '@/components/shared/EmptyState';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { SearchBar } from '@/components/shared/SearchBar';
import { FilterBar, FilterConfig } from '@/components/shared/FilterBar';
import { Pagination } from '@/components/shared/Pagination';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { SortSelector, SortOption } from '@/components/shared/SortSelector';
import { ViewToggle, ViewMode } from '@/components/shared/ViewToggle';
import { ExportButton } from '@/components/shared/ExportButton';
import { Button } from '@/components/ui/Button';
import { X } from 'lucide-react';

const ITEMS_PER_PAGE = 9;

export default function BarbersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { barbers, loading, deleteBarber, updateBarber, listBarbers } = useBarber();
  const { franchises, listFranchises } = useFranchise();
  const { branches, listBranches } = useBranch();
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [sortBy, setSortBy] = useState('displayName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  // Modal para crear barbero
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedFranchiseForCreate, setSelectedFranchiseForCreate] = useState('');
  const [selectedBranchForCreate, setSelectedBranchForCreate] = useState('');

  useEffect(() => {
    listBarbers();
    // Solo cargar franquicias y sucursales si es super_admin
    if (user?.role === 'super_admin') {
      listFranchises();
      listBranches();
    }
  }, [user]);

  // Construir opciones de filtro dinámicamente
  const filterConfigs: FilterConfig[] = useMemo(() => {
    const configs: FilterConfig[] = [];

    // Filtro de franquicia (solo para super_admin)
    if (user?.role === 'super_admin' && franchises && franchises.length > 0) {
      configs.push({
        key: 'franchiseId',
        label: 'Franquicia',
        options: franchises.map(f => ({
          value: f.franchiseId,
          label: f.name
        })),
      });
    }

    // Filtro de sucursal
    if (branches && branches.length > 0) {
      const branchOptions = activeFilters.franchiseId
        ? branches
            .filter(b => b.franchiseId === activeFilters.franchiseId)
            .map(b => ({ value: b.branchId, label: b.name }))
        : branches.map(b => ({ value: b.branchId, label: b.name }));

      if (branchOptions.length > 0) {
        configs.push({
          key: 'branchId',
          label: 'Sucursal',
          options: branchOptions,
        });
      }
    }

    // Filtros existentes
    configs.push(
      {
        key: 'isAvailable',
        label: 'Disponibilidad',
        options: [
          { value: 'true', label: 'Disponible' },
          { value: 'false', label: 'No disponible' },
        ],
      },
      {
        key: 'isActive',
        label: 'Estado',
        options: [
          { value: 'true', label: 'Activo' },
          { value: 'false', label: 'Inactivo' },
        ],
      }
    );

    return configs;
  }, [user, franchises, branches, activeFilters.franchiseId]);

  const sortOptions: SortOption[] = [
    { value: 'displayName', label: 'Name' },
    { value: 'createdAt', label: 'Date Created' },
  ];

  const filteredBarbers = useMemo(() => {
    let result = barbers || [];

    // Search filter
    if (searchQuery) {
      result = result.filter(b =>
        b.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.specialties.some(s => s.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (b.bio && b.bio.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Filtro de franquicia
    if (activeFilters.franchiseId) {
      result = result.filter(b => b.franchiseId === activeFilters.franchiseId);
    }

    // Filtro de sucursal
    if (activeFilters.branchId) {
      result = result.filter(b => b.branchId === activeFilters.branchId);
    }

    // Active filters
    if (activeFilters.isAvailable) {
      result = result.filter(b => b.isAvailable === (activeFilters.isAvailable === 'true'));
    }
    if (activeFilters.isActive) {
      result = result.filter(b => b.isActive === (activeFilters.isActive === 'true'));
    }

    // Sorting
    result.sort((a, b) => {
      let aValue: any = a[sortBy as keyof typeof a];
      let bValue: any = b[sortBy as keyof typeof b];

      // Handle Firestore Timestamps
      if (sortBy === 'createdAt' && aValue?.toDate && bValue?.toDate) {
        aValue = aValue.toDate().getTime();
        bValue = bValue.toDate().getTime();
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [barbers, searchQuery, activeFilters, sortBy, sortDirection]);

  // Pagination
  const totalPages = Math.ceil((filteredBarbers?.length || 0) / ITEMS_PER_PAGE);
  const paginatedBarbers = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredBarbers?.slice(start, end);
  }, [filteredBarbers, currentPage]);

  const handleDelete = async () => {
    if (!deleteId) return;
    await deleteBarber(deleteId);
    setDeleteId(null);
  };

  const handleToggleAvailability = async (barberId: string, isAvailable: boolean) => {
    await updateBarber(barberId, { isAvailable });
  };

  const handleFilterChange = (filters: Record<string, string>) => {
    setActiveFilters(filters);
    setCurrentPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  // Filtrar branches por franchise seleccionada en el modal
  const filteredBranchesForCreate = useMemo(() => {
    if (!selectedFranchiseForCreate) return [];
    return (branches || []).filter(b => b.franchiseId === selectedFranchiseForCreate);
  }, [branches, selectedFranchiseForCreate]);

  const handleOpenCreateModal = () => {
    setSelectedFranchiseForCreate('');
    setSelectedBranchForCreate('');
    setShowCreateModal(true);
  };

  const handleConfirmCreate = () => {
    if (selectedFranchiseForCreate && selectedBranchForCreate) {
      navigate(`/barbers/new?franchiseId=${selectedFranchiseForCreate}&branchId=${selectedBranchForCreate}`);
      setShowCreateModal(false);
    }
  };

  if (loading) {
    return <LoadingState message="Loading barbers..." variant="skeleton" />;
  }

  return (
    <div className="container mx-auto p-6">
      {/* Breadcrumbs */}
      <Breadcrumbs items={[{ label: 'Barbers' }]} />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-ink">Barbers</h1>
          <p className="text-ink-muted mt-1">
            {filteredBarbers?.length || 0} total {filteredBarbers?.length === 1 ? 'barber' : 'barbers'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ExportButton data={filteredBarbers || []} filename="barbers" />
          <ViewToggle view={viewMode} onChange={setViewMode} />
          <Button onClick={handleOpenCreateModal}>
            + New Barber
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <SearchBar
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search barbers by name, specialties, or bio..."
        />
      </div>

      {/* Filter Bar and Sort */}
      <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <FilterBar
          filters={filterConfigs}
          onFilterChange={handleFilterChange}
          onReset={() => setSearchQuery('')}
        />
        <SortSelector
          options={sortOptions}
          value={sortBy}
          direction={sortDirection}
          onChange={setSortBy}
          onDirectionChange={setSortDirection}
        />
      </div>

      {/* Content */}
      {!paginatedBarbers || paginatedBarbers.length === 0 ? (
        <EmptyState
          icon="💈"
          title="No barbers found"
          message={searchQuery || Object.keys(activeFilters).length > 0
            ? "No barbers match your search or filters. Try adjusting your criteria."
            : "Get started by creating your first barber."}
          actionLabel="Create Barber"
          onAction={handleOpenCreateModal}
        />
      ) : (
        <>
          {/* Grid or List View */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 mb-6">
              {paginatedBarbers.map((barber) => (
                <BarberCard
                  key={barber.barberId}
                  barber={barber}
                  onEdit={(id) => navigate(`/barbers/${id}/edit`)}
                  onDelete={setDeleteId}
                  onToggleAvailability={handleToggleAvailability}
                />
              ))}
            </div>
          ) : (
            <div className="mb-6">
              <BarberListView
                barbers={paginatedBarbers}
                onEdit={(id) => navigate(`/barbers/${id}/edit`)}
                onDelete={setDeleteId}
                onToggleAvailability={handleToggleAvailability}
                showEmail={user?.role === 'super_admin'}
              />
            </div>
          )}

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredBarbers?.length || 0}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </>
      )}

      <ConfirmDialog
        open={!!deleteId}
        title="Delete Barber"
        message="Are you sure you want to delete this barber? This action cannot be undone."
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onClose={() => setDeleteId(null)}
      />

      {/* Modal para seleccionar franquicia y sucursal antes de crear */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-hairline">
              <h3 className="text-xl font-semibold text-ink">
                Crear Nuevo Barbero
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 hover:bg-surface-2 rounded"
              >
                <X className="w-5 h-5 text-ink-subtle" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-ink-muted mb-4">
                Selecciona la franquicia y sucursal donde se creara el barbero.
              </p>
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">
                  Franquicia *
                </label>
                <select
                  value={selectedFranchiseForCreate}
                  onChange={(e) => {
                    setSelectedFranchiseForCreate(e.target.value);
                    setSelectedBranchForCreate('');
                  }}
                  className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
                >
                  <option value="">Selecciona una franquicia</option>
                  {(franchises || []).map((franchise) => (
                    <option key={franchise.franchiseId} value={franchise.franchiseId}>
                      {franchise.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">
                  Sucursal *
                </label>
                <select
                  value={selectedBranchForCreate}
                  onChange={(e) => setSelectedBranchForCreate(e.target.value)}
                  disabled={!selectedFranchiseForCreate}
                  className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {selectedFranchiseForCreate ? 'Selecciona una sucursal' : 'Primero selecciona una franquicia'}
                  </option>
                  {filteredBranchesForCreate.map((branch) => (
                    <option key={branch.branchId} value={branch.branchId}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-hairline flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm font-medium text-ink-muted bg-surface-2 rounded-lg hover:bg-surface-3 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmCreate}
                disabled={!selectedFranchiseForCreate || !selectedBranchForCreate}
                className="px-4 py-2 text-sm font-medium text-white bg-info rounded-lg hover:bg-info/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
